import { useState, KeyboardEvent } from 'react';
import { Lock, X, Check } from 'lucide-react';

interface PasswordDialogProps {
  isOpen: boolean;
  title?: string;
  hint?: string;
  onSubmit: (password: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
}

export function PasswordDialog({
  isOpen,
  title = 'Enter Password',
  hint,
  onSubmit,
  onCancel,
  isLoading = false,
  isError = false,
  isSuccess = false,
}: PasswordDialogProps) {
  const [password, setPassword] = useState('');

  const handleSubmit = () => {
    onSubmit(password);
    setPassword('');
  };

  const handleCancel = () => {
    setPassword('');
    onCancel();
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
      <div
        className={`bg-white border border-gray-300 rounded-lg shadow-lg w-96 ${
          isError ? 'animate-shake' : ''
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Lock size={18} strokeWidth={1.5} className="text-gray-700" />
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={16} strokeWidth={1.5} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        {isSuccess ? (
          <div className="p-12 flex flex-col items-center justify-center">
            <div className="animate-check-bounce">
              <Check size={48} strokeWidth={2} className="text-gray-900" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              Access Granted
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e as KeyboardEvent)}
              disabled={isLoading || isSuccess}
              className={`w-full px-3 py-2 border rounded text-sm outline-none transition-colors disabled:bg-gray-50 ${
                isError
                  ? 'border-red-500 hover:border-red-600 focus:border-red-600'
                  : 'border-gray-300 hover:border-gray-400 focus:border-gray-500'
              }`}
            />
            {hint && <p className="text-xs text-gray-600">Hint: {hint}</p>}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !password}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying...' : 'Unlock'}
          </button>
        </div>
      </div>
    </div>
  );
}
